import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { ChevronLeft, ChevronRight, RefreshCw, Search, X } from 'lucide-react';
import styled from 'styled-components';
import {
  Button, Card, CardTitle, ChipClear, Empty, Field, FilterChip, Input, Message, Select,
} from '../admin/ui';
import * as Api from './EmailAdminApi';
import {
  SIZE, STATUSES, TRIGGERS, errorText, eventTitle, groupLabel, logParams, statusLabel, triggerLabel,
  typeGroups, typeLabel, viennaTime,
} from './emailAdminModel';

// The "Sent" view of the E-Mails screen: the log of every email the system sent, newest first, with
// the filters the backend offers (type, event, a free search, status, trigger, a Vienna date range)
// and paging. Server-side: the log service filters and pages, so the screen holds only the query it
// sent. A failed row says why on click (the full error is also the button's hover title).
//
// Every call goes through ./EmailAdminApi; a 4xx/5xx answers a plain-text body that is what the
// admin should read.

const EMPTY_FILTERS = { type: '', eventId: '', q: '', status: '', trigger: '', from: '', to: '' };

const StatusTag = styled.span`
  display: inline-block;
  padding: .15rem .5rem;
  font-size: .68rem;
  font-weight: 800;
  border-radius: 999px;
  background: ${(p) => (p.$failed ? '#fee2e2' : '#dcfce7')};
  color: ${(p) => (p.$failed ? '#991b1b' : '#166534')};
`;

const ErrorToggle = styled.button`
  display: block;
  margin-top: .3rem;
  border: 0;
  padding: 0;
  background: transparent;
  color: #991b1b;
  font: inherit;
  font-size: .72rem;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
  text-decoration: underline;
`;

const ErrorText = styled.div`
  margin-top: .25rem;
  padding: .3rem .45rem;
  max-width: 18rem;
  background: #fef2f2;
  border-left: 3px solid #dc2626;
  color: #7f1d1d;
  font-size: .72rem;
  word-break: break-word;
`;

const Th = ({ children }) => (
  <th style={{ padding: '.5rem .6rem', fontWeight: 700, borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }}>{children}</th>
);
const Td = ({ children }) => <td style={{ padding: '.5rem .6rem', verticalAlign: 'top' }}>{children}</td>;

const SentEmails = ({ initialType = '' }) => {
  const intl = useIntl();
  const t = useCallback((id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values), [intl]);

  // The "show sent emails of this type" link opens the log already filtered by the template's
  // MailType. The filters stay local - the URL carries the tab, never the query.
  const [filters, setFilters] = useState(() => (initialType ? { ...EMPTY_FILTERS, type: initialType } : EMPTY_FILTERS));
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(0);

  const [data, setData] = useState(null);
  const [types, setTypes] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openError, setOpenError] = useState(null);

  const seq = useRef(0);
  const params = useMemo(() => logParams(filters, { page, size: SIZE }), [filters, page]);

  const load = useCallback(async () => {
    const token = ++seq.current;
    setLoading(true);
    setError(null);
    try {
      const result = await Api.fetchLog(params);
      if (seq.current === token) setData(result);
    } catch (err) {
      if (seq.current === token) setError(err);
    } finally {
      if (seq.current === token) setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    Api.fetchTypes().then((list) => setTypes(Array.isArray(list) ? list : [])).catch(() => setTypes([]));
    Api.fetchEventOptions().then((options) => setEvents(options || [])).catch(() => setEvents([]));
  }, []);

  // Any filter change starts again at the first page.
  const applyFilters = useCallback((change) => {
    setFilters((current) => ({ ...current, ...change }));
    setPage(0);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setSearchInput('');
    setPage(0);
  }, []);

  const submitSearch = useCallback((event) => {
    event.preventDefault();
    applyFilters({ q: searchInput.trim() });
  }, [applyFilters, searchInput]);

  const filterCount = ['type', 'eventId', 'q', 'status', 'trigger', 'from', 'to']
    .filter((key) => filters[key] !== '').length;
  const groups = typeGroups(types);
  const rows = data?.rows || [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / SIZE));

  return (
    <>
      {error && <Message error role="alert">{errorText(error, t('emailAdmin.load.failed', 'Could not load the emails.'))}</Message>}

      <Card>
        <CardTitle><FormattedMessage id="emailAdmin.filters.title" defaultMessage="Find emails" /></CardTitle>
        <form onSubmit={submitSearch} style={{ display: 'flex', gap: '.6rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Field style={{ minWidth: '18rem', flex: '1 1 18rem' }}>
            <FormattedMessage id="emailAdmin.search" defaultMessage="Search" />
            <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('emailAdmin.searchPlaceholder', 'Subject, name or address')} />
          </Field>
          <Button type="submit"><Search size={16} /><FormattedMessage id="emailAdmin.search.action" defaultMessage="Search" /></Button>
          <Button type="button" secondary disabled={loading} onClick={load}>
            <RefreshCw size={16} /><FormattedMessage id="emailAdmin.refresh" defaultMessage="Refresh" />
          </Button>
          {filterCount > 0 && (
            <Button type="button" secondary onClick={clearFilters}>
              <X size={16} /><FormattedMessage id="emailAdmin.filters.clear" defaultMessage="Clear filters" />
            </Button>
          )}
        </form>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(11rem, 1fr))', gap: '.9rem', marginTop: '1rem' }}>
          <Field>
            <FormattedMessage id="emailAdmin.filter.type" defaultMessage="Type" />
            <Select value={filters.type} onChange={(e) => applyFilters({ type: e.target.value })}>
              <option value="">{t('emailAdmin.filter.any', 'Any')}</option>
              {groups.map(({ group, types: groupTypes }) => (
                <optgroup key={group} label={groupLabel(t, group)}>
                  {groupTypes.map((type) => (
                    <option key={type.key} value={type.key}>{typeLabel(t, type.key)}</option>
                  ))}
                </optgroup>
              ))}
            </Select>
          </Field>
          <Field>
            <FormattedMessage id="emailAdmin.filter.event" defaultMessage="Event" />
            <Select value={filters.eventId} onChange={(e) => applyFilters({ eventId: e.target.value })}>
              <option value="">{t('emailAdmin.filter.any', 'Any')}</option>
              {events.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.startsOn ? `${eventTitle(intl.locale, event)} — ${event.startsOn}` : eventTitle(intl.locale, event)}
                </option>
              ))}
            </Select>
          </Field>
          <Field>
            <FormattedMessage id="emailAdmin.filter.status" defaultMessage="Status" />
            <Select value={filters.status} onChange={(e) => applyFilters({ status: e.target.value })}>
              <option value="">{t('emailAdmin.filter.any', 'Any')}</option>
              {STATUSES.map((status) => (
                <option key={status} value={status}>{statusLabel(t, status)}</option>
              ))}
            </Select>
          </Field>
          <Field>
            <FormattedMessage id="emailAdmin.filter.trigger" defaultMessage="Trigger" />
            <Select value={filters.trigger} onChange={(e) => applyFilters({ trigger: e.target.value })}>
              <option value="">{t('emailAdmin.filter.any', 'Any')}</option>
              {TRIGGERS.map((trigger) => (
                <option key={trigger} value={trigger}>{triggerLabel(t, trigger)}</option>
              ))}
            </Select>
          </Field>
          <Field>
            <FormattedMessage id="emailAdmin.filter.from" defaultMessage="From" />
            <Input type="date" value={filters.from} onChange={(e) => applyFilters({ from: e.target.value })} />
          </Field>
          <Field>
            <FormattedMessage id="emailAdmin.filter.to" defaultMessage="To" />
            <Input type="date" value={filters.to} onChange={(e) => applyFilters({ to: e.target.value })} />
          </Field>
        </div>

        {filterCount > 0 && (
          <FilterChip style={{ marginTop: '.9rem', marginBottom: 0 }}>
            <FormattedMessage id="emailAdmin.filters.active" defaultMessage="{count, plural, one {# filter} other {# filters}} active"
              values={{ count: filterCount }} />
            <ChipClear type="button" onClick={clearFilters} aria-label={t('emailAdmin.filters.clear', 'Clear filters')}><X size={14} /></ChipClear>
          </FilterChip>
        )}
      </Card>

      <Card>
        {loading && !data ? (
          <Empty><FormattedMessage id="emailAdmin.loading" defaultMessage="Loading emails…" /></Empty>
        ) : rows.length === 0 ? (
          <Empty><FormattedMessage id="emailAdmin.empty" defaultMessage="No email matches these filters." /></Empty>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.85rem' }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#475569' }}>
                    <Th><FormattedMessage id="emailAdmin.col.time" defaultMessage="Time" /></Th>
                    <Th><FormattedMessage id="emailAdmin.col.type" defaultMessage="Type" /></Th>
                    <Th><FormattedMessage id="emailAdmin.col.recipient" defaultMessage="Recipient" /></Th>
                    <Th><FormattedMessage id="emailAdmin.col.event" defaultMessage="Event" /></Th>
                    <Th><FormattedMessage id="emailAdmin.col.subject" defaultMessage="Subject" /></Th>
                    <Th><FormattedMessage id="emailAdmin.col.trigger" defaultMessage="Trigger" /></Th>
                    <Th><FormattedMessage id="emailAdmin.col.status" defaultMessage="Status" /></Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} style={{ borderTop: '1px solid #eef2f7' }}>
                      <Td><span title={row.sentAt || ''} style={{ whiteSpace: 'nowrap' }}>{viennaTime(row.sentAt)}</span></Td>
                      <Td>{typeLabel(t, row.type)}</Td>
                      <Td>
                        {row.personId ? (
                          <Link to={`/admin/people/${row.personId}`} style={{ fontWeight: 700, color: '#111D4E' }}>
                            {row.personName || `#${row.personId}`}
                          </Link>
                        ) : (
                          <span>{row.personName || <FormattedMessage id="emailAdmin.recipient.unknown" defaultMessage="No person" />}</span>
                        )}
                        <div style={{ fontSize: '.75rem', color: '#64748b', wordBreak: 'break-all' }}>{row.toAddress}</div>
                      </Td>
                      <Td>{row.eventName || '—'}</Td>
                      <Td>{row.subject || '—'}</Td>
                      <Td>{triggerLabel(t, row.trigger, row.sentByName)}</Td>
                      <Td>
                        <StatusTag $failed={row.status === 'FAILED'}>{statusLabel(t, row.status)}</StatusTag>
                        {row.status === 'FAILED' && row.error && (
                          <>
                            <ErrorToggle type="button" title={row.error} aria-expanded={openError === row.id}
                              onClick={() => setOpenError(openError === row.id ? null : row.id)}>
                              {openError === row.id
                                ? t('emailAdmin.error.hide', 'Hide error')
                                : t('emailAdmin.error.show', 'Show error')}
                            </ErrorToggle>
                            {openError === row.id && <ErrorText role="note">{row.error}</ErrorText>}
                          </>
                        )}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', marginTop: '1rem', flexWrap: 'wrap' }}>
              <Button type="button" secondary disabled={loading || page <= 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                <ChevronLeft size={16} /><FormattedMessage id="emailAdmin.page.prev" defaultMessage="Previous" />
              </Button>
              <span style={{ fontSize: '.82rem', color: '#475569' }}>
                <FormattedMessage id="emailAdmin.page.of"
                  defaultMessage="Page {page} of {pages} — {total, plural, one {# email} other {# emails}}"
                  values={{ page: page + 1, pages: totalPages, total }} />
              </span>
              <Button type="button" secondary disabled={loading || page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>
                <FormattedMessage id="emailAdmin.page.next" defaultMessage="Next" /><ChevronRight size={16} />
              </Button>
            </div>
          </>
        )}
      </Card>
    </>
  );
};

export default SentEmails;
